<template>
  <div>
    <!-- Hide entire section if no available meal/venue/time -->
    <div class="form-group row" v-show="!isMealsLoading &&
      facilityInfo.length > 0 &&
      facilityInfo[0]?.status == 'enabled'">
      <label class="col-sm-2 col-form-label">
        {{ $t('visit-information-form.meal_reservation.label_meal_reservation') }}
      </label>

      <div class="col-sm-10 my-auto" v-if="effectiveHasTables">
        <!-- Loop through meal types -->
        <div v-for="(item, index) in availableMeals" :key="`${item.meal_type_id}_${item.date}_${index}`">

          <!-- Venue selection -->
          <div class="my-2">
            <label>
              {{ $t(`visit-information-form.meal_reservation.${mapMealType(item.meal_type_id)}`) ?? "N/A" }}
              {{ $t('visit-information-form.meal_reservation.venue') }}
              ({{ formatDate(item.date) }})
            </label>
            <small class="text-danger">*</small>
            <div class="my-auto">
              <select v-if="!confirm" v-model="selectedVenues[index]" class="form-control" @change="updateSelections"
                :class="{ 'is-invalid': validationErrors[`venue_${index}`] }">
                <option v-for="venue in getVenuesForMeal(item.meal_type_id, item.date)" :key="venue.venueId"
                  :value="venue.venueId">
                  {{ $i18n.locale === 'ja' ? venue.name_jp : venue.name_en }}
                </option>
              </select>
              <p v-if="confirm">
                {{ getVenueName(selectedVenues[index]) }}
              </p>
            </div>
            <div v-if="validationErrors[`venue_${index}`]" class="invalid-feedback d-block">
              {{ validationErrors[`venue_${index}`] }}
            </div>
          </div>

          <!-- Time slot selection -->
          <div class="my-4">
            <label>
              {{ $t(`visit-information-form.meal_reservation.${mapMealType(item.meal_type_id)}`) ?? 'N/A' }}
              {{ $t('visit-information-form.meal_reservation.time') }}
              ({{ formatDate(item.date) }})
            </label>
            <small class="text-danger">*</small>
            <div class="my-auto">
              <select v-if="!confirm" v-model="selectedTimes[index]" class="form-control" @change="updateSelections"
                :class="{ 'is-invalid': validationErrors[`time_${index}`] }">
                <option v-for="slot in getTimeSlots(item.meal_type_id, selectedVenues[index], item.date)"
                  :key="`${slot.start}:${slot.end}:${index}`" :value="slot.start">
                  {{ slot.start }} - {{ slot.end }}
                </option>
              </select>
              <p v-if="confirm">
                {{ selectedTimes[index] }} -
                {{ getEndTime(selectedTimes[index], item.meal_type_id, selectedVenues[index], item.date) }}
              </p>
            </div>
            <div v-if="validationErrors[`time_${index}`]" class="invalid-feedback d-block">
              {{ validationErrors[`time_${index}`] }}
            </div>
          </div>

        </div>
      </div>

      <div v-else class="col-sm-10 my-auto">
        <div class="border rounded p-3">
          <div class="form-check d-flex align-items-start">
            <input v-if="!confirm" class="form-check-input mt-1 me-2" type="checkbox" id="mealCheck"
              v-model="noMealConfirmed" :class="{ 'is-invalid': showMealError }" @change="onNoMealChange" :required="!effectiveHasTables && !confirm && facilityInfo[0]?.status == 'enabled'" />

            <!-- When confirm = true: show frozen checkbox -->
            <input v-else class="form-check-input mt-1 me-2" type="checkbox" id="mealCheck" :checked="noMealConfirmed"
              disabled style="cursor: not-allowed;" />

            <label for="mealCheck" class="form-check-label text-muted">
              {{ $t('visit-information-form.meal_reservation.no_tables_notice') }}
            </label>
          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';

export default {
  name: 'MealComponent',
  props: {
    facility: Object,
    companions: Array,
    mealDetails: Object,
    confirm: Boolean,
    hasAvailableTables: {
      type: Boolean,
      default: true
    },
    dailySettings: { type: Object, default: null },
    booking_details: {
      type: Object
    }
  },
  data() {
    return {
      selectedVenues: [],
      selectedTimes: [],
      facilityInfo: [],
      guestCount: 1,
      validationErrors: {},
      venueMeals: {},
      showMealError: false,
      noMealConfirmed: false,
      localHasAvailableTables: this.hasAvailableTables,
      isMealsLoading: true
    };
  },
  computed: {
    ...mapState('memorizedInputs', ['checkinFormType']),
    ...mapState('guest', ['numberOfGuest', 'guestCountSetting', 'bookingPlanCode']),

    effectiveHasTables() {
      return this.localHasAvailableTables;
    },

    sortedMeals() {
      const meals = this.mealDetails?.meal;

      if (!Array.isArray(meals)) return [];

      const grouped = meals.reduce((acc, item) => {
        const key = `${item.date}_${item.meal_type_id}`;
        if (!acc[key]) {
          acc[key] = {
            date: item.date,
            meal_type_id: item.meal_type_id,
            meal_names: []
          };
        }
        acc[key].meal_names.push(item.meal_name);
        return acc;
      }, {});

      return Object.values(grouped)
        .sort((a, b) => new Date(a.date) - new Date(b.date));
    },

    availableMeals() {
      const meals = this.sortedMeals;

      let allMealsHaveVenues = true;

      const mappedMeals = meals.map(meal => {
        const mealTypeId = meal.meal_type_id;
        const date = meal.date;

        const venues = this.getVenuesForMeal(mealTypeId, date) || [];

        // 🚨 CRITICAL RULE:
        // If ANY meal has no venues → entire dropdown system disabled
        if (venues.length === 0) {
          allMealsHaveVenues = false;
        }

        return {
          ...meal,
          venues
        };
      });

      // Final decision
      this.localHasAvailableTables = allMealsHaveVenues;
      this.$emit('update:hasAvailableTables', allMealsHaveVenues);

      return mappedMeals;
    },

  },

  async mounted() {
    await this.initVenues();
  },
  methods: {
    async initVenues() {
      try {
        this.isMealsLoading = true;

        // Step 1: Ensure facility info is fetched first
        if (!this.facilityInfo.length) {
          await this.fetchFacilityInMrs(this.facility.id);
        }

        // Step 2: Wait until meal details are ready
        if (!this.mealDetails || !Array.isArray(this.mealDetails.meal) || !this.mealDetails.meal.length) {
          console.warn("Waiting for meal details...");
          const unwatch = this.$watch(
            'mealDetails.meal',
            async (meals) => {
              if (Array.isArray(meals) && meals.length > 0) {
                await this.fetchVenues(this.facilityInfo[0]?.id);
                //this.selectedSlots();
                this.ensureSelectedSlots();
                this.isMealsLoading = false;
                unwatch();
              }
            },
            { deep: true, immediate: true }
          );
          return;
        }

        // Step 3: Once both are ready, fetch venues
        await this.fetchVenues(this.facilityInfo[0]?.id);
        this.selectedSlots(); // TOBE: remove if not really needed

        this.ensureSelectedSlots();
        this.isMealsLoading = false;

      } catch (err) {
        console.error("Error initializing venues:", err);
        this.isMealsLoading = false;
      }
    },

    onNoMealChange() {
      if (this.noMealConfirmed) {
        this.showMealError = false;
        this.$emit('no-meal-confirmed', true);
      }
    },

    isMealTypeFullyBooked(venue, mealTypeId, date) {
      const mealType = this.mapMealType(mealTypeId)
      const slots = venue.details?.time_slots?.[mealType] || [];

      if (!slots.length) return false; // no slots defined = not fully booked?

      // A meal type is fully booked if **all its time slots include the date in dates_fully_booked**
      return slots.every(slot => {
        const fullyBookedDates = slot.dates_fully_booked || [];
        return fullyBookedDates.includes(date);
      });
    },

    getVenuesForMeal(mealTypeId, date) {
      const mealType = this.mapMealType(mealTypeId);
      const venues = this.venueMeals?.[date]?.[mealType] || [];

      return venues.filter(v => {
        const details = v.details || v.originalDetails;

        if (!details?.meal_type?.includes(mealType)) return false;

        const slots = details?.time_slots?.[mealType] || [];

        return slots.some(slot => {
          const fullyBookedDates = slot.dates_fully_booked || [];
          return !fullyBookedDates.includes(date);
        });
      });
    },

    getTimeSlots(mealTypeId, venueId, date) {
      if (!venueId) return [];

      const mealType = this.mapMealType(mealTypeId);
      const venues = this.venueMeals?.[date]?.[mealType] || [];
      const venue = venues.find(v => v.venueId === venueId);
      if (!venue) return [];

      const details = venue.details || venue.originalDetails;
      const slots = details?.time_slots?.[mealType] || [];

      const filterBookedTimeSlots = slots.filter(slot => {
        const fullyBookedDates = Array.isArray(slot.dates_fully_booked)
          ? slot.dates_fully_booked
          : [];

        return !fullyBookedDates.includes(date);
      });

      return filterBookedTimeSlots.slice().sort((a, b) => {
        const startCompare = a.start.localeCompare(b.start);
        return startCompare !== 0
          ? startCompare
          : a.end.localeCompare(b.end);
      });
    },

    mapMealType(typeId) {
      return { 1: 'breakfast', 2: 'lunch', 3: 'dinner' }[typeId];
    },

    getVenueName(venueId) {
      for (const date in this.venueMeals) {
        const mealsForDate = this.venueMeals[date];

        for (const mealType in mealsForDate) {
          const venues = mealsForDate[mealType] || [];

          const venue = venues.find(v => v.venueId === venueId);
          if (venue) {
            return this.$i18n.locale === 'ja'
              ? venue.name_jp
              : venue.name_en;
          }
        }
      }

      return 'Not available';
    },

    getEndTime(startTime, mealTypeId, venueId, date) {
      const mealType = this.mapMealType(mealTypeId);
      const venues = this.venueMeals?.[date]?.[mealType] || [];
      const venue = venues.find(v => v.venueId === venueId);
      if (!venue) return 'N/A';

      const details = venue.details || venue.originalDetails;

      const slot = details?.time_slots?.[mealType]?.find(
        s => s.start === startTime
      );

      return slot?.end || 'N/A';
    },

    formatDate(date) {
      return date ? date.replace(/-/g, '/') : 'N/A';
    },

    getGuestCount() {
      return this.numberOfGuest;
    },

    // --- SELECTIONS ---
    selectedSlots() {
      const count = this.mealDetails?.meal?.length || 0;
      this.selectedVenues = Array(count).fill(null);
      this.selectedTimes = Array(count).fill(null);
    },

    validateSelections() {
      this.validationErrors = {};
      this.sortedMeals.forEach((item, index) => {
        if (!this.selectedVenues[index]) this.validationErrors[`venue_${index}`] = this.$t('visit-information-form.meal_reservation.venue_error');
        if (!this.selectedTimes[index]) this.validationErrors[`time_${index}`] = this.$t('visit-information-form.meal_reservation.time_error');
      });
      return Object.keys(this.validationErrors).length === 0;
    },

    updateSelections() {
      this.validationErrors = {};
      const selections = this.sortedMeals.reduce((acc, item, index) => {

        const mealType = this.mapMealType(item.meal_type_id);
        const venueId = this.selectedVenues[index];
        const startTime = this.selectedTimes[index];

        if (!acc[item.date]) acc[item.date] = {};
        if (!acc[item.date][mealType]) acc[item.date][mealType] = [];

        this.guestCount = this.getGuestCount();

        const menuString = Array.isArray(item.meal_names)
          ? item.meal_names.join(", ")
          : item.meal_names || item.meal_name || "";

        acc[item.date][mealType].push({
          venue_id: venueId,
          time_slot: startTime ? { start_time: startTime, end_time: this.getEndTime(startTime, item.meal_type_id, venueId, item.date) } : null,
          number_of_pax: this.guestCount,
          menu: menuString
        });
        return acc;
      }, {});

      this.$emit('mealSelection', selections);
    },

    // --- API FETCH ---
    async fetchFacilityInMrs(facilityId) {
      try {
        const response = await this.$mrsApi.get(`/facility?checkin_facility_id=${facilityId}`);
        this.facilityInfo = response.data;
        await this.fetchVenues(this.facilityInfo[0]?.id);
      } catch (error) {
        console.error('Error fetching facility info:', error);
      }
    },

    async fetchAllVenues(facilityId) {

      const bookingData = this.booking_details.data[0] || [];
      const startDate = bookingData.checkin_date;
      const endDate = bookingData.checkout_date;
      const pax = this.getGuestCount();

      try {
        const { data } = await this.$mrsApi.get(`/venue?facility_id=${facilityId}&reservation_s_date=${startDate}&reservation_e_date=${endDate}&filter=1&is_cis=true&pax=${pax}`);
        return data.map(v => ({
          venueId: v.id,
          name_en: v.name_en || 'N/A',
          name_jp: v.name_jp || 'N/A',
          details: {
            meal_type: v.details.meal_type || [],
            time_slots: {
              breakfast: v.details.time_slots?.breakfast || [],
              lunch: v.details.time_slots?.lunch || [],
              dinner: v.details.time_slots?.dinner || []
            },
            tables: v.details.tables || [],
            business_hours: v.details.business_hours || {}
          }
        }));
      } catch (err) {
        console.error('Error fetching all venues:', err);
        return [];
      }
    },

    async fetchVenuesViaPlanCode(facilityId, planCode) {
      const bookingData = this.booking_details.data[0] || [];
      const startDate = bookingData.checkin_date;
      const endDate = bookingData.checkout_date;
      const pax = this.getGuestCount();

      try {
        const { data } = await this.$mrsApi
          .get(`/venue-filter?facility_id=${facilityId}&reservation_s_date=${startDate}&reservation_e_date=${endDate}&plan_code=${planCode}&filter=1&is_cis=true&pax=${pax}`);

        return data || [];
      } catch (err) {
        console.error('Error fetching venues via plan code:', err);
        return [];
      }
    },

    async fetchDailySettings(facilityId) {
      const bookingData = this.booking_details.data[0] || [];
      const startDate = bookingData.checkin_date;
      const endDate = bookingData.checkout_date;
      const pax = this.getGuestCount();

      try {
        const { data } = await this.$mrsApi
          .get(`/venue-setting?facility_id=${facilityId}&reservation_s_date=${startDate}&reservation_e_date=${endDate}&filter=1&is_cis=true&pax=${pax}`);
        return data || [];
      } catch (err) {
        console.error('Error fetching daily settings:', err);
        return [];
      }
    },

    async fetchVenues(facilityId) {
      if (!facilityId) {
        console.warn("fetchVenues skipped: facilityId is undefined")
        this.venueMeals = {};
        return;
      }


      try {
        const context = await this.loadVenueContext(facilityId);
        const venueMeals = this.buildVenueMeals(context);
        this.$set(this, 'venueMeals', venueMeals);
      } catch (err) {
        console.error("Error fetching venues: ", err)
        this.venueMeals = {};
      }
    },

    async loadVenueContext(facilityId) {
      const [allVenues, dailySettings] = await Promise.all([
        this.fetchAllVenues(facilityId),
        this.fetchDailySettings(facilityId),
      ]);

      // for plan code
      const planCode = this.bookingPlanCode || `npc${facilityId}`;
      const planData = await this.fetchVenuesViaPlanCode(facilityId, planCode);

      const venueMap = this.defaultVenues(allVenues);
      const dailySettingsVenueMap = this.dailySettingsVenues(dailySettings, venueMap);
      const planCodeVenueMap = this.planCodeVenues(planData, venueMap);

      return {
        allVenues,
        venueMap,
        dailySettingsVenueMap,
        planCodeVenueMap,
        meals: Array.isArray(this.mealDetails?.meal) ? this.mealDetails.meal : [],
      }
    },

    defaultVenues(allVenues) {
      return allVenues.reduce((acc, v) => {
        acc[v.venueId] = {
          ...v,
          originalDetails: { ...v.details },
          dailySettings: {},
        };
        return acc;
      }, {});
    },

    dailySettingsVenues(dailySettings, venueMap) {
      const byDate = {};

      dailySettings.forEach(ds => {
        if (!byDate[ds.start_date]) byDate[ds.start_date] = [];
        byDate[ds.start_date].push(ds);

        const venue = venueMap[ds.venue_id];
        if (venue) {
          venue.dailySettings[ds.start_date] = {
            id: ds.id,
            start_date: ds.start_date,
            details: ds.details,
          };
        }
      });

      return byDate;
    },

    planCodeVenues(planData, venueMap) {
      const planVenueMap = {};

      const planEntry = Array.isArray(planData) ? planData[0] : null;
      if (!planEntry?.venue_timeslot) return planVenueMap;

      planEntry.venue_timeslot.forEach(slotGroup => {
        Object.entries(slotGroup).forEach(([mealType, meal]) => {
          if (!meal?.timeslot?.length || !meal?.id) return;

          const venue = venueMap[meal.id];
          if (!venue) return;

          if (!planVenueMap[meal.id]) {
            planVenueMap[meal.id] = {
              ...venue,
              details: {
                meal_type: [],
                time_slots: { breakfast: [], lunch: [], dinner: [] },
              },
            };
          }

          planVenueMap[meal.id].details.time_slots[mealType].push(...meal.timeslot);

          if (!planVenueMap[meal.id].details.meal_type.includes(mealType)) {
            planVenueMap[meal.id].details.meal_type.push(mealType);
          }
        });
      });

      return planVenueMap;
    },

    buildVenueMeals({
      meals,
      allVenues,
      venueMap,
      dailySettingsVenueMap,
      planCodeVenueMap,
    }) {
      const venueMeals = {};

      meals.forEach(mealForm => {
        const date = mealForm.date;
        const mealType = this.mapMealType(mealForm.meal_type_id);

        if (!venueMeals[date]) venueMeals[date] = {};
        if (!venueMeals[date][mealType]) venueMeals[date][mealType] = [];

        // resolve all venues for this date + mealType
        const resolvedVenues = this.resolveVenuesForDateMeal(
          date,
          mealType,
          allVenues,
          venueMap,
          dailySettingsVenueMap,
          planCodeVenueMap
        );

        venueMeals[date][mealType] = resolvedVenues;
      });

      return venueMeals;
    },

    resolveVenuesForDateMeal(
      date,
      mealType,
      allVenues,
      venueMap,
      dailySettingsVenueMap,
      planCodeVenueMap
    ) {
      const result = [];
      const hasDaily = !!dailySettingsVenueMap[date];
      const hasPlan = Object.keys(planCodeVenueMap).length > 0;

      allVenues.forEach(originalVenue => {
        let finalDetails = originalVenue.originalDetails || originalVenue.details;

        const venueId = originalVenue.venueId;

        // --- APPLY DAILY SETTINGS (override base) ---
        if (hasDaily) {
          const dailyEntry = dailySettingsVenueMap[date].find(ds => ds.venue_id === venueId);

          if (dailyEntry) {
            finalDetails = dailyEntry.details;

            // --- NEW CHECK: If this mealType is disabled in daily settings, skip this venue ---
            if (!finalDetails?.meal_type?.includes(mealType)) {
              return; // skip this venue for this mealType/date
            }
          }
        }

        // --- APPLY PLAN (intersect with current state) ---
        if (hasPlan) {
          const planVenue = planCodeVenueMap[venueId];
          if (!planVenue) return; // not allowed by plan

          finalDetails = this.intersectDetails(
            finalDetails,
            planVenue.details,
            mealType
          );
        }

        // --- VALIDATION ---
        if (this.isValidMeal(finalDetails, mealType)) {
          result.push({
            ...originalVenue,
            details: finalDetails
          });
        }
      });

      return result;
    },

    intersectDetails(baseDetails, planDetails, mealType) {
      const baseSlots = baseDetails?.time_slots?.[mealType] || [];
      const planSlots = planDetails?.time_slots?.[mealType] || [];

      const intersectedSlots = baseSlots.filter(b =>
        planSlots.some(p => p.start === b.start && p.end === b.end)
      );

      return {
        meal_type: intersectedSlots.length > 0 ? [mealType] : [],
        time_slots: {
          breakfast: mealType === 'breakfast' ? intersectedSlots : [],
          lunch: mealType === 'lunch' ? intersectedSlots : [],
          dinner: mealType === 'dinner' ? intersectedSlots : [],
        }
      };
    },

    updateAvailableTables() {
      // Default: show meal dropdowns
      let showMealDropdowns = true;

      // Loop all meals
      for (const meal of this.availableMeals) {
        const venues = this.getVenuesForMeal(meal.meal_type_id, meal.date) || [];

        // If **no venues available for this meal**, force MRS checkbox
        if (venues.length === 0) {
          showMealDropdowns = false;
          break;
        }

        // check fully booked (current logic)
        for (const venue of venues) {
          if (this.isMealTypeFullyBooked(venue, meal.meal_type_id, meal.date)) {
            showMealDropdowns = false;
            break;
          }
        }

        if (!showMealDropdowns) break;
      }

      this.localHasAvailableTables = showMealDropdowns;
      this.$emit('update:hasAvailableTables', showMealDropdowns);
    },

    isValidMeal(details, mealType) {
      return (
        details?.meal_type?.includes(mealType) &&
        details?.time_slots?.[mealType]?.length > 0
      );
    },

    ensureSelectedSlots() {
      const count = this.sortedMeals.length;

      while (this.selectedVenues.length < count) {
        this.selectedVenues.push(null);
      }

      while (this.selectedTimes.length < count) {
        this.selectedTimes.push(null);
      }
    }

  },
  watch: {
    hasAvailableTables(newVal) {
      this.localHasAvailableTables = newVal; // keep in sync if parent changes
    },
    mealDetails: {
      deep: true,
      handler() {
        this.updateAvailableTables();
      }
    },
    companions: {
      deep: true,
      handler() {
        this.guestCount = this.getGuestCount();
        this.updateSelections();
      }
    },
    venueMeals: {
      deep: true,
      handler() {
        this.updateAvailableTables();
        this.$nextTick(() => {

          this.ensureSelectedSlots()
          this.updateSelections();
        });
      }
    },
    '$i18n.locale'() {
      this.validateSelections();
    },
    async bookingPlanCode(newVal) {
      // wait for facilityInfo to load first
      if (!this.facilityInfo.length) {
        const unwatch = this.$watch('facilityInfo', async (val) => {
          if (val.length) {
            await this.fetchVenues(val[0].id);
            unwatch();
          }
        });
      } else {
        await this.fetchVenues(this.facilityInfo[0].id);
      }
    }
  }
};
</script>

<style scoped>
/* Style checkbox when invalid */
.is-invalid {
  border: 1px solid #dc3545;
}

/* Ensure the error message below aligns nicely */
.invalid-feedback {
  color: #dc3545;
}
</style>